'use client';

import { CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/Skeleton';

export function AppLineChart({
  chartData,
  config = {},
  innerData,
  dataKey, // No default value as requested
  nameKey, // No default value as requested
  isLoading = false,
}: {
  chartData: any[];
  config?: ChartConfig;
  innerData?: { value: number | string; title: string };
  dataKey: string; // Required prop
  nameKey: string; // Required prop
  isLoading?: boolean;
}) {
  const data = chartData?.map(item => {
    const itemKey = item[nameKey] as string;
    return {
      ...item,
      fill: config[itemKey]?.color || `var(--color-${dataKey})`,
    };
  });

  return (
    <div className="w-full overflow-hidden">
      {isLoading ? (
        <div className="p-4">
          <Skeleton className="h-64 w-full rounded-md bg-coal-200" />
        </div>
      ) : (
        <>
          <ChartContainer config={config} className="aspect-auto h-[300px] w-full">
            <LineChart
              accessibilityLayer
              data={data}
              margin={{
                top: 24,
                left: 20,
                right: 20,
              }}
            >
              <XAxis
                tickMargin={2}
                tickLine={false}
                padding={{ left: 20, right: 20 }}
                dataKey={nameKey}
              />
              <YAxis tickMargin={2} tickLine={false} padding={{ top: 20 }} />
              <CartesianGrid vertical={false} />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent indicator="line" nameKey={dataKey} hideLabel />}
              />
              <Line
                dataKey={dataKey}
                type="natural"
                stroke={`var(--color-${dataKey})`}
                strokeWidth={2}
                dot={props => {
                  const itemKey = props?.payload?.[nameKey] as string;
                  const customColor = config[itemKey]?.color;
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={4}
                      fill={customColor || `var(--color-${dataKey})`}
                      stroke="none"
                    />
                  );
                }}
                activeDot={props => {
                  const itemKey = props?.payload?.[nameKey] as string;
                  const customColor = config[itemKey]?.color;
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={6}
                      fill={customColor || `var(--color-${dataKey})`}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  );
                }}
              >
                {innerData && (
                  <LabelList
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            <tspan
                              x={viewBox.cx}
                              y={viewBox.cy}
                              className="fill-foreground text-4xl font-bold"
                            >
                              {innerData?.value || ''}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 24}
                              className="fill-muted-foreground text-base"
                            >
                              {innerData?.title || ''}
                            </tspan>
                          </text>
                        );
                      }
                      return null;
                    }}
                  />
                )}
                <LabelList
                  position="top"
                  offset={12}
                  className="fill-foreground"
                  fontSize={12}
                  dataKey={nameKey}
                  formatter={(value: string) => config[value]?.label || value}
                />
              </Line>
            </LineChart>
          </ChartContainer>
        </>
      )}
    </div>
  );
}
